import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Facebook, Instagram, Upload, Edit2, MapPin, 
  Building, Clock, Save, X, Camera, AlertCircle,
  CheckCircle2, Loader2
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import LocationAutocomplete from '../../components/LocationAutocomplete';
import SedeLocation from '../../components/register/SedeLocation';
import PropertyManager from './PropertyManager';

// Constantes para URLs de Cloudflare
const CLOUDFLARE_PROFILES_URL = 'https://pub-2a10bd66114a4441b95e9249adee01cf.r2.dev';
const CLOUDFLARE_BANNERS_URL = 'https://pub-cdaec3d9feef44cf8950eee9a93266e4.r2.dev';

// Lista de servicios de construcción
const SERVICIOS_CONSTRUCCION = [
  'Construcción residencial',
  'Construcción comercial',
  'Proyectos industriales',
  'Remodelaciones',
  'Diseño arquitectónico',
  'Gestión de proyectos',
  'Consultoría técnica',
  'Acabados de lujo',
  'Servicios de excavación',
  'Instalaciones especializadas'
];

const DEFAULT_BUSINESS_HOURS = {
  lunes_viernes: { open: '09:00', close: '18:00', isOpen: true },
  sabado: { open: '09:00', close: '13:00', isOpen: true },
  domingo: { open: '00:00', close: '00:00', isOpen: false }
};

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      duration: 0.6, 
      ease: [0.25, 0.46, 0.45, 0.94]
    } 
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

// Utility Components
const EditSection = ({ title, children, icon: Icon }) => (
  <motion.div 
    className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg dark:shadow-black/20 p-6 md:p-8 border border-amber-100 dark:border-gray-700"
    variants={fadeInUp}
  >
    <div className="flex items-center gap-3 mb-6">
      {Icon && (
        <div className="p-2 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-xl">
          <Icon className="w-5 h-5 text-white" />
        </div>
      )}
      <h2 className="text-xl md:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-amber-600 to-yellow-600 dark:from-amber-400 dark:to-yellow-400">
        {title}
      </h2>
    </div>
    {children}
  </motion.div>
);

const EditProfileImage = ({ src, onChange }) => {
  const [preview, setPreview] = useState(src);
  const [dragOver, setDragOver] = useState(false);

  const handleFileChange = (file) => {
    if (file) {
      setPreview(URL.createObjectURL(file));
      onChange({ target: { files: [file] } });
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      handleFileChange(file);
    }
  };

  return (
    <div className="flex flex-col items-center">
      <div 
        className={`relative w-32 h-32 md:w-40 md:h-40 rounded-full border-4 border-dashed transition-all duration-300 ${
          dragOver 
            ? 'border-amber-400 bg-amber-50 dark:bg-amber-900/20' 
            : 'border-amber-200 dark:border-amber-700 hover:border-amber-300 dark:hover:border-amber-600'
        }`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
      >
        {preview ? (
          <img 
            src={preview} 
            alt="Profile" 
            className="w-full h-full rounded-full object-cover"
          />
        ) : (
          <div className="w-full h-full rounded-full bg-gradient-to-br from-amber-100 to-yellow-100 dark:from-amber-900/30 dark:to-yellow-900/30 flex items-center justify-center">
            <Camera className="w-8 h-8 text-amber-400 dark:text-amber-500" />
          </div>
        )}
        
        <motion.label
          htmlFor="profileImage"
          className="absolute bottom-2 right-2 p-2 bg-amber-500 text-white rounded-full cursor-pointer shadow-lg hover:bg-amber-600 transition-colors"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
        >
          <Edit2 className="w-4 h-4" />
          <input
            id="profileImage"
            type="file"
            accept="image/*"
            onChange={(e) => handleFileChange(e.target.files[0])}
            className="hidden"
          />
        </motion.label>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 text-center">
        Arrastra una imagen aquí o haz clic para subir
      </p>
    </div>
  );
};

const ActionButton = ({ children, onClick, variant = "primary", disabled = false, icon: Icon, className = "" }) => {
  const variants = {
    primary: "bg-gradient-to-r from-amber-500 to-yellow-600 text-white shadow-lg hover:shadow-xl dark:shadow-xl dark:hover:shadow-2xl",
    secondary: "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 border border-gray-200 dark:border-gray-600",
    danger: "bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg hover:shadow-xl dark:shadow-xl dark:hover:shadow-2xl"
  };

  return (
    <motion.button
      className={`
        ${variants[variant]} 
        px-6 py-3 rounded-xl font-medium transition-all duration-300 
        flex items-center gap-2 justify-center
        focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800
        disabled:opacity-50 disabled:cursor-not-allowed
        ${className}
      `}
      onClick={onClick}
      disabled={disabled}
      whileHover={!disabled ? { scale: 1.02, y: -2 } : {}}
      whileTap={!disabled ? { scale: 0.98 } : {}}
    >
      {Icon && <Icon className="w-5 h-5" />}
      {children}
    </motion.button>
  );
};

// Función mejorada para procesar imágenes de perfil
const getProfileImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  
  // Caso especial: cuando la URL contiene tanto localhost como la URL de Cloudflare
  if (imageUrl.includes('https://backend-production-1786.up.railway.app/profiles/https://pub-') && imageUrl.includes('r2.dev')) {
    // Extraer solo la parte de Cloudflare
    return imageUrl.substring(imageUrl.indexOf('https://pub-'));
  }
  
  // Si ya es una URL de Cloudflare, usarla directamente
  if (imageUrl.includes('r2.dev')) {
    return imageUrl;
  }
  
  // Si es una URL local completa del servidor
  if (imageUrl.includes('https://backend-production-1786.up.railway.app/profiles/')) {
    const filename = imageUrl.split('/profiles/').pop();
    return `${CLOUDFLARE_PROFILES_URL}/${filename}`;
  }
  
  // Si es una ruta relativa con /profiles/
  if (imageUrl.includes('/profiles/')) {
    const filename = imageUrl.split('/profiles/').pop();
    return `${CLOUDFLARE_PROFILES_URL}/${filename}`;
  }
  
  // Si es solo el nombre del archivo
  return `${CLOUDFLARE_PROFILES_URL}/${imageUrl}`;
};

// Función mejorada para procesar banners
const getBannerImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  
  // Caso especial: cuando la URL contiene tanto localhost como la URL de Cloudflare
  if (imageUrl.includes('https://backend-production-1786.up.railway.app/banners/https://pub-') && imageUrl.includes('r2.dev')) {
    // Extraer solo la parte de Cloudflare
    return imageUrl.substring(imageUrl.indexOf('https://pub-'));
  }
  
  // Si ya es una URL de Cloudflare, usarla directamente
  if (imageUrl.includes('r2.dev')) {
    return imageUrl;
  }
  
  // Si es una URL local completa del servidor
  if (imageUrl.includes('https://backend-production-1786.up.railway.app/banners/')) {
    const filename = imageUrl.split('/banners/').pop();
    return `${CLOUDFLARE_BANNERS_URL}/${filename}`;
  }
  
  // Si es una ruta relativa con /banners/
  if (imageUrl.includes('/banners/')) {
    const filename = imageUrl.split('/banners/').pop();
    return `${CLOUDFLARE_BANNERS_URL}/${filename}`;
  }
  
  // Si es solo el nombre del archivo
  return `${CLOUDFLARE_BANNERS_URL}/${imageUrl}`;
};

// Función para parsear horarios de atención
const parseBusinessHours = (horariosStr) => {
  try {
    // Si ya es un objeto, devolverlo
    if (typeof horariosStr === 'object' && horariosStr !== null) {
      return {
        lunes_viernes: horariosStr.lunes_viernes || DEFAULT_BUSINESS_HOURS.lunes_viernes,
        sabado: horariosStr.sabado || DEFAULT_BUSINESS_HOURS.sabado,
        domingo: horariosStr.domingo || DEFAULT_BUSINESS_HOURS.domingo
      };
    }

    // Si es un string, intentar parsearlo
    if (typeof horariosStr === 'string') {
      const parsed = JSON.parse(horariosStr);
      return {
        lunes_viernes: parsed.lunes_viernes || DEFAULT_BUSINESS_HOURS.lunes_viernes,
        sabado: parsed.sabado || DEFAULT_BUSINESS_HOURS.sabado,
        domingo: parsed.domingo || DEFAULT_BUSINESS_HOURS.domingo
      };
    }

    // Si no se puede parsear, devolver valores por defecto
    return DEFAULT_BUSINESS_HOURS;
  } catch (error) {
    console.error('Error parseando horarios:', error);
    return DEFAULT_BUSINESS_HOURS;
  }
};

const EditConsProfile = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // Estados principales
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Estados para imágenes
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState(null);
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);

  // Estados del perfil
  const [profileData, setProfileData] = useState({
    nombre: '',
    nombre_empresa: '',
    ruc: '',
    slogan: '',
    experiencia: '',
    anos_experiencia: '',
    sobre_nosotros: '',
    facebook: '',
    instagram: '',
    ubicacion: '',
    direccion_sede: '',
    sitio_web: '',
    servicios: [],
    horario_atencion: DEFAULT_BUSINESS_HOURS
  });

  // Estado para propiedades destacadas
  const [featuredPropertyIds, setFeaturedPropertyIds] = useState([]);

  // Manejadores de cambios
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({ ...prev, [name]: value }));
    setHasUnsavedChanges(true);
  };

  const handleServiceChange = (service) => {
    setProfileData(prev => {
      const newServices = prev.servicios.includes(service)
        ? prev.servicios.filter(s => s !== service)
        : [...prev.servicios, service];
      
      // Limitar a 6 servicios
      const finalServices = newServices.slice(0, 6);
      
      return { 
        ...prev, 
        servicios: finalServices 
      };
    });
    setHasUnsavedChanges(true);
  };

  const handleLocationSelect = (location) => {
    setProfileData(prev => ({
      ...prev,
      ubicacion: `${location.city}, ${location.province}, ${location.country}`,
      ciudad: location.city,
      provincia: location.province,
      pais: location.country
    }));
    setHasUnsavedChanges(true);
  };

  const handleSedeLocationSelect = (location) => {
    setProfileData(prev => ({
      ...prev,
      direccion_sede: location.formatted_address
    }));
    setHasUnsavedChanges(true);
  };

  const handleFeaturedPropertiesUpdate = (selectedIds) => {
    setFeaturedPropertyIds(selectedIds);
    setHasUnsavedChanges(true);
  };

  const handleBannerChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBannerFile(file);
      setBannerPreview(URL.createObjectURL(file));
      setHasUnsavedChanges(true);
    }
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setProfileImageFile(file);
      setProfileImagePreview(URL.createObjectURL(file));
      setHasUnsavedChanges(true);
    }
  };

  const handleHorarioChange = (dia, campo, valor) => {
    console.log(`Cambiando horario - Día: ${dia}, Campo: ${campo}, Valor: ${valor}`);
    
    setProfileData(prev => {
      const nuevoHorario = {
        ...prev.horario_atencion,
        [dia]: {
          ...prev.horario_atencion[dia],
          [campo]: valor
        }
      };
      
      console.log('Nuevo horario completo:', nuevoHorario);
      
      return {
        ...prev,
        horario_atencion: nuevoHorario
      };
    });
    setHasUnsavedChanges(true);
  };

  //Cargar Datos del Perfil
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        const token = localStorage.getItem('token');
        
        if (!token) {
          throw new Error('No se encontró token de autenticación');
        }
  
        console.log('Iniciando carga de perfil detallado');
        
        const response = await fetch('https://backend-production-1786.up.railway.app/api/constructoras/perfil-detallado', {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
  
        console.log('Respuesta del servidor:', response);
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('Detalles del error:', errorText);
          throw new Error(`Error al cargar el perfil: ${response.status} ${errorText}`);
        }
  
        const data = await response.json();
        
        // Depuración
        console.log('Datos de perfil detallado recibidos:', data);
  
        // Actualizar estado con los datos recibidos
        setProfileData(prevData => {
          const updatedData = {
            ...prevData,
            nombre: data.nombre || '',
            nombre_empresa: data.nombre_empresa || '',
            ruc: data.ruc || '',
            slogan: data.slogan || '',
            sobre_nosotros: data.sobre_nosotros || '',
            sitio_web: data.slug || '',
            experiencia: data.experiencia || '',
            anos_experiencia: data.anos_experiencia || '',
            direccion_sede: data.direccion_sede || '',
            ubicacion: data.ubicacion || '',
            facebook: data.facebook || '',
            instagram: data.instagram || '',
            whatsapp: data.whatsapp || '',
            servicios: data.servicios || [],
            // Parseo seguro de horarios
            horario_atencion: parseBusinessHours(data.horario_atencion || DEFAULT_BUSINESS_HOURS)
          };
  
          console.log('Datos actualizados:', updatedData);
          return updatedData;
        });
  
        // Procesar URLs de imágenes correctamente usando las funciones mejoradas
        if (data.foto_perfil) {
          const profileUrl = getProfileImageUrl(data.foto_perfil);
          console.log('URL de imagen de perfil procesada:', profileUrl);
          setProfileImagePreview(profileUrl);
        }
  
        if (data.banner_image) {
          const bannerUrl = getBannerImageUrl(data.banner_image);
          console.log('URL de banner procesada:', bannerUrl);
          setBannerPreview(bannerUrl);
        }
  
      } catch (error) {
        // Registro de error más detallado
        console.error('Error completo al cargar datos del perfil:', {
          name: error.name,
          message: error.message,
          stack: error.stack
        });
        
        // Notificar al usuario de manera amigable
        setError(`No se pudieron cargar los datos del perfil: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };
  
    fetchProfileData();
  }, []);

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      
      // Añadir archivos si se han cambiado
      if (bannerFile) formData.append('banner', bannerFile);
      if (profileImageFile) formData.append('profile_image', profileImageFile);
  
      // Añadir el resto de datos
      Object.keys(profileData).forEach(key => {
        if (['horario_atencion', 'servicios'].includes(key)) {
          formData.append(key, JSON.stringify(profileData[key]));
        } else {
          formData.append(key, profileData[key]);
        }
      });
  
      // Actualizar perfil
      const response = await fetch('https://backend-production-1786.up.railway.app/api/vendedores/perfil', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
  
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Error al guardar los cambios');
      }

      // Actualizar propiedades destacadas
      if (featuredPropertyIds.length > 0) {
        await fetch('https://backend-production-1786.up.railway.app/api/vendedores/featured-properties', {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ propertyIds: featuredPropertyIds })
        });
      }
  
      setHasUnsavedChanges(false);
      setSuccess(true);
      
      // Mostrar mensaje de éxito y redirigir después de un momento
      setTimeout(() => {
        navigate('/mi_perfil');
      }, 2000);
      
    } catch (error) {
      setError(error.message);
      console.error('Error al guardar:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      if (window.confirm('¿Estás seguro de que quieres cancelar? Se perderán los cambios no guardados.')) {
        navigate('/mi_perfil');
      }
    } else {
      navigate('/mi_perfil');
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-6 bg-gradient-to-br from-amber-50 via-white to-yellow-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <motion.div
          className="relative"
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        >
          <div className="w-24 h-24 border-4 border-amber-200 dark:border-amber-800 rounded-full">
            <div className="absolute top-0 left-0 w-24 h-24 border-4 border-amber-600 dark:border-amber-400 rounded-full border-t-transparent animate-spin"></div>
          </div>
        </motion.div>
        <motion.div
          className="text-center"
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          <p className="text-xl font-semibold text-amber-700 dark:text-amber-300 mb-2">Cargando perfil...</p>
          <p className="text-gray-600 dark:text-gray-400">Obteniendo información de la constructora</p>
        </motion.div>
      </div>
    );
  }

  // Renderizado principal
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white dark:from-gray-900 dark:to-gray-800">
      {/* Header con banner */}
      <div className="relative h-64 md:h-80 overflow-hidden">
        <div className="absolute inset-0">
          {bannerPreview ? (
            <img 
              src={bannerPreview} 
              alt="Banner" 
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-r from-amber-600 via-amber-500 to-yellow-600" />
          )}
          <div className="absolute inset-0 bg-black/40" />
        </div>
        
        {/* Banner upload button */}
        <motion.label
          htmlFor="bannerUpload"
          className="absolute top-4 right-4 p-3 bg-black/30 backdrop-blur-sm text-white rounded-xl cursor-pointer hover:bg-black/40 transition-colors"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Upload className="w-5 h-5" />
          <input
            id="bannerUpload"
            type="file"
            accept="image/*"
            onChange={handleBannerChange}
            className="hidden"
          />
        </motion.label>

        {/* Title */}
        <div className="absolute bottom-6 left-6">
          <motion.h1 
            className="text-3xl md:text-4xl font-bold text-white mb-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Editar Perfil de Constructora
          </motion.h1>
          <motion.p 
            className="text-white/90"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Configura la información de tu empresa constructora
          </motion.p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-8 -mt-16 relative z-10">
        {/* Status Messages */}
        {error && (
          <motion.div 
            className="mb-6 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-xl flex items-center gap-3"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400" />
            <p className="text-red-700 dark:text-red-300">{error}</p>
          </motion.div>
        )}

        {success && (
          <motion.div 
            className="mb-6 p-4 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-xl flex items-center gap-3"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <CheckCircle2 className="w-5 h-5 text-green-500 dark:text-green-400" />
            <p className="text-green-700 dark:text-green-300">
              ¡Perfil actualizado exitosamente! Redirigiendo...
            </p>
          </motion.div>
        )}

        <motion.div 
          className="space-y-8"
          variants={staggerContainer}
          initial="hidden"
          animate="visible"
        >
          {/* Información de la Empresa */}
          <EditSection title="Información de la Empresa" icon={Building}>
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-shrink-0">
                <EditProfileImage
                  src={profileImagePreview}
                  onChange={handleProfileImageChange}
                />
              </div>
              
              <div className="flex-1 space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Nombre de la Constructora
                    </label>
                    <input
                      type="text"
                      name="nombre_empresa"
                      value={profileData.nombre_empresa || ''}
                      onChange={handleInputChange}
                      className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200"
                      placeholder="Nombre de la constructora"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      RUC
                    </label>
                    <input
                      type="text"
                      name="ruc"
                      value={profileData.ruc || ''}
                      onChange={handleInputChange}
                      maxLength="11"
                      className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200"
                      placeholder="RUC"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Slogan
                  </label>
                  <input
                    type="text"
                    name="slogan"
                    value={profileData.slogan || ''}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200"
                    placeholder="Slogan de la constructora"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Sitio Web
                  </label>
                  <input
                    type="url"
                    name="sitio_web"
                    value={profileData.sitio_web || ''}
                    onChange={handleInputChange}
                    className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200"
                    placeholder="https://www.ejemplo.com"
                  />
                </div>
              </div>
            </div>
          </EditSection>

          {/* About Section */}
          <EditSection title="Sobre Nosotros" icon={Edit2}>
            <div>
              <textarea
                name="sobre_nosotros"
                value={profileData.sobre_nosotros || ''}
                onChange={handleInputChange}
                placeholder="Cuéntanos más sobre tu constructora..."
                className="w-full p-4 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent min-h-[150px] bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200 resize-vertical"
              />
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Esta información aparecerá en tu perfil público.
              </p>
            </div>
          </EditSection>

          {/* Ubicación y Sede */}
          <EditSection title="Ubicación y Sede" icon={MapPin}>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Ubicación General
                </label>
                <LocationAutocomplete 
                  onLocationSelect={handleLocationSelect}
                  initialValue={profileData.ubicacion}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Dirección de la Sede Principal
                </label>
                <SedeLocation 
                  location={{
                    direccion_sede: profileData.direccion_sede
                  }}
                  onLocationSelect={handleSedeLocationSelect}
                />
              </div>
            </div>
          </EditSection>

          {/* Servicios Ofrecidos */}
          <EditSection title="Servicios Ofrecidos" icon={Building}>
            <div className="space-y-6">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Selecciona hasta 6 servicios principales que ofrece tu constructora
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {SERVICIOS_CONSTRUCCION.map((servicio) => (
                  <motion.label
                    key={servicio}
                    className={`flex items-center p-4 border rounded-xl cursor-pointer transition-all duration-200 hover:shadow-md ${
                      profileData.servicios.includes(servicio) 
                        ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/30 dark:border-amber-600' 
                        : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    <input
                      type="checkbox"
                      checked={profileData.servicios.includes(servicio)}
                      onChange={() => handleServiceChange(servicio)}
                      className="hidden"
                    />
                    <div 
                      className={`w-5 h-5 mr-3 border-2 rounded transition-all duration-200 flex items-center justify-center ${
                        profileData.servicios.includes(servicio) 
                          ? 'bg-amber-500 border-amber-500' 
                          : 'border-gray-300 dark:border-gray-500'
                      }`}
                    >
                      {profileData.servicios.includes(servicio) && (
                        <motion.svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="white" 
                          strokeWidth="3"
                          className="w-3 h-3"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ type: "spring", stiffness: 500 }}
                        >
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </motion.svg>
                      )}
                    </div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{servicio}</span>
                  </motion.label>
                ))}
              </div>
              {profileData.servicios.length >= 6 && (
                <motion.p 
                  className="text-amber-600 dark:text-amber-400 text-sm font-medium"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  Has alcanzado el límite máximo de 6 servicios
                </motion.p>
              )}
            </div>
          </EditSection>

          {/* Horarios de Atención */}
          <EditSection title="Horarios de Atención" icon={Clock}>
            <div className="space-y-6">
              {Object.entries({
                'lunes_viernes': 'Lunes a Viernes',
                'sabado': 'Sábado',
                'domingo': 'Domingo'
              }).map(([key, label]) => (
                <div key={key} className="p-4 border border-gray-200 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700/50">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    {label}
                  </label>
                  <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={profileData.horario_atencion[key].isOpen}
                        onChange={() => handleHorarioChange(key, 'isOpen', 
                          !profileData.horario_atencion[key].isOpen)}
                        className="w-4 h-4 text-amber-600 rounded border-gray-300 dark:border-gray-500 focus:ring-amber-500 dark:bg-gray-600"
                      />
                      <span className="ml-2 text-sm text-gray-600 dark:text-gray-400 font-medium">
                        {profileData.horario_atencion[key].isOpen ? 'Abierto' : 'Cerrado'}
                      </span>
                    </label>

                    {profileData.horario_atencion[key].isOpen && (
                      <div className="flex gap-4 items-center">
                        <input
                          type="time"
                          value={profileData.horario_atencion[key].open}
                          onChange={(e) => handleHorarioChange(key, 'open', e.target.value)}
                          className="p-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        />
                        <span className="text-gray-500 dark:text-gray-400 font-medium">a</span>
                        <input
                          type="time"
                          value={profileData.horario_atencion[key].close}
                          onChange={(e) => handleHorarioChange(key, 'close', e.target.value)}
                          className="p-2 border border-gray-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </EditSection>

          {/* Propiedades Destacadas */}
          <PropertyManager 
            onPropertyUpdate={handleFeaturedPropertiesUpdate}
          />

          {/* Redes Sociales */}
          <EditSection title="Redes Sociales">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-xl">
                  <Facebook className="w-6 h-6 text-blue-500 dark:text-blue-400" />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Facebook
                  </label>
                  <input
                    type="text"
                    name="facebook"
                    value={profileData.facebook}
                    onChange={handleInputChange}
                    placeholder="Usuario de Facebook"
                    className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <div className="p-3 bg-pink-50 dark:bg-pink-900/30 rounded-xl">
                  <Instagram className="w-6 h-6 text-pink-500 dark:text-pink-400" />
                </div>
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Instagram
                  </label>
                  <input
                    type="text"
                    name="instagram"
                    value={profileData.instagram}
                    onChange={handleInputChange}
                    placeholder="Usuario de Instagram"
                    className="w-full p-3 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 transition-all duration-200"
                  />
                </div>
              </div>
            </div>
          </EditSection>
        </motion.div>

        {/* Action Buttons */}
        <motion.div 
          className="flex flex-col sm:flex-row gap-4 justify-end mt-8 p-6 bg-white dark:bg-gray-800 rounded-2xl shadow-lg dark:shadow-black/20 border border-gray-200 dark:border-gray-700"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <ActionButton
            variant="secondary"
            onClick={handleCancel}
            disabled={saving}
            icon={X}
            className="sm:w-auto w-full"
          >
            Cancelar
          </ActionButton>
          
          <ActionButton
            variant="primary"
            onClick={handleSave}
            disabled={saving || !hasUnsavedChanges}
            icon={saving ? Loader2 : Save}
            className="sm:w-auto w-full"
          >
            {saving ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Guardando...
              </>
            ) : (
              'Guardar Cambios'
            )}
          </ActionButton>
        </motion.div>
      </div>
    </div>
  );
};

export default EditConsProfile;